import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IconButton } from '@/design-system/icon-button';
import { SearchSelect } from '@/design-system/search-select';
import { Sheet, SheetContent, SheetTitle } from '@/design-system/sheet';
import { toast } from '@/design-system/toaster';
import { Pitstop, getListPitstop } from '@/services/api/pitstops';
import {
  InboundItem,
  ScanItemSuccessResponse,
  getInboundItems,
  scanItem,
} from '@/services/api/inbound';
import {
  isScannerKeystroke,
  looksLikeUrlInput,
  trackCodeFromScan,
} from '@/utils';

// ── Audio ────────────────────────────────────────────────────────────────────

// Shared context + oscillator tracker — prevents overlapping beeps on rapid scans
let _audioCtx: AudioContext | null = null;
const _liveOscillators: OscillatorNode[] = [];

function getAudioCtx(): AudioContext {
  if (!_audioCtx || _audioCtx.state === 'closed') {
    _audioCtx = new (
      window.AudioContext || (window as any).webkitAudioContext
    )();
  }
  if (_audioCtx.state === 'suspended') {
    _audioCtx.resume();
  }
  return _audioCtx;
}

function stopAllOscillators() {
  for (const osc of _liveOscillators) {
    try {
      osc.stop();
    } catch (_) {}
  }
  _liveOscillators.length = 0;
}

function playTone(
  ctx: AudioContext,
  dest: AudioNode,
  freq: number,
  startTime: number,
  duration: number,
  peakGain: number,
  type: OscillatorType = 'sine'
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startTime);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(peakGain, startTime + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  osc.connect(gain);
  gain.connect(dest);
  _liveOscillators.push(osc);
  osc.onended = () => {
    const i = _liveOscillators.indexOf(osc);
    if (i !== -1) _liveOscillators.splice(i, 1);
  };
  osc.start(startTime);
  osc.stop(startTime + duration + 0.05);
}

function playBeep(type: 'success' | 'duplicate' | 'error') {
  try {
    // Cut any currently-playing beep before starting the new one
    stopAllOscillators();

    const ctx = getAudioCtx();

    // Compressor prevents clipping while allowing loud, punchy output
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-3, ctx.currentTime);
    compressor.knee.setValueAtTime(3, ctx.currentTime);
    compressor.ratio.setValueAtTime(6, ctx.currentTime);
    compressor.attack.setValueAtTime(0.001, ctx.currentTime);
    compressor.release.setValueAtTime(0.15, ctx.currentTime);
    compressor.connect(ctx.destination);

    const t = ctx.currentTime;

    if (type === 'success') {
      playTone(ctx, compressor, 880, t, 0.1, 0.7, 'sine');
      playTone(ctx, compressor, 1760, t, 0.1, 0.35, 'sine');
      playTone(ctx, compressor, 1174, t + 0.09, 0.14, 0.65, 'sine');
      playTone(ctx, compressor, 2349, t + 0.09, 0.14, 0.3, 'sine');
    } else if (type === 'duplicate') {
      playTone(ctx, compressor, 660, t, 0.09, 0.65, 'square');
      playTone(ctx, compressor, 660, t + 0.13, 0.09, 0.6, 'square');
    } else {
      playTone(ctx, compressor, 520, t, 0.1, 0.7, 'sawtooth');
      playTone(ctx, compressor, 390, t + 0.09, 0.1, 0.65, 'sawtooth');
      playTone(ctx, compressor, 260, t + 0.18, 0.14, 0.6, 'sawtooth');
    }
  } catch (_) {
    // AudioContext not available — silent fallback
  }
}

// ── Voice announcement ───────────────────────────────────────────────────────

const VOICE_ENABLED_KEY = 'scanpanel_voice_enabled';

function getVoiceEnabledDefault(): boolean {
  try {
    return localStorage.getItem(VOICE_ENABLED_KEY) !== 'false';
  } catch {
    return true;
  }
}

function playAnnouncement(text: string) {
  try {
    if (!('speechSynthesis' in window)) return;
    // Cancel any queued utterance so rapid scans don't stack up
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'id-ID';
    utter.rate = 1.4;
    utter.pitch = 1.0;
    utter.volume = 1.0;
    window.speechSynthesis.speak(utter);
  } catch (_) {
    // speechSynthesis not available — silent fallback
  }
}

// ── Types ────────────────────────────────────────────────────────────────────

type ScanState = 'idle' | 'success' | 'zone-not-found' | 'duplicate' | 'error';

interface ScanEntry {
  id: string;
  po: string;
  message: string;
  state: ScanState;
  time: string;
  zoneCode?: string | null;
  zoneName?: string | null;
  zoneColor?: string | null;
}

interface ScanPanelProps {
  isOpen: boolean;
  onClose: () => void;
  hubs: Pitstop[];
  selectedHubId: number | null;
  onHubChange: (hubId: number | null) => void;
  onScanSuccess: () => void;
}

const MAX_FEED = 20;

function nowTime() {
  return new Date().toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

// ── Component ────────────────────────────────────────────────────────────────

const ScanPanel: React.FC<ScanPanelProps> = ({
  isOpen,
  onClose,
  hubs,
  selectedHubId,
  onHubChange,
  onScanSuccess,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scanningRef = useRef(false);

  const [localHubId, setLocalHubId] = useState<number | null>(selectedHubId);
  // Hub dropdown is server-searchable: options are fetched from the API with a
  // debounced `search` param rather than filtered locally. Seeded from the
  // parent-provided `hubs` so the dropdown is populated before the first search.
  const [hubOptions, setHubOptions] = useState<Pitstop[]>(hubs);
  const [hubLoading, setHubLoading] = useState(false);
  const hubSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [scanning, setScanning] = useState(false);
  const [flashState, setFlashState] = useState<ScanState>('idle');
  const [feed, setFeed] = useState<ScanEntry[]>([]);
  const [lastScan, setLastScan] = useState<ScanEntry | null>(null);
  const [inputText, setInputText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<InboundItem[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Scanner-gun detection for the suggestion search — see handleInputChange.
  const lastInputChangeAtRef = useRef(0);
  const prevInputValueRef = useRef('');
  const [voiceEnabled, setVoiceEnabled] = useState<boolean>(
    getVoiceEnabledDefault
  );

  const toggleVoice = () => {
    setVoiceEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(VOICE_ENABLED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // Keep hub options in sync with the parent-provided list (initial / refetch)
  useEffect(() => {
    setHubOptions(hubs);
  }, [hubs]);

  // Clean up the hub-search debounce on unmount
  useEffect(() => {
    return () => {
      if (hubSearchDebounceRef.current) {
        clearTimeout(hubSearchDebounceRef.current);
      }
    };
  }, []);

  // Keep local hub in sync with parent
  useEffect(() => {
    setLocalHubId(selectedHubId);
  }, [selectedHubId]);

  // Auto-focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 230);
    }
  }, [isOpen]);

  // Re-focus after each scan completes
  useEffect(() => {
    if (!scanning) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [scanning]);

  const handleHubChange = (value: string) => {
    const id = value ? Number(value) : null;
    setLocalHubId(id);
    onHubChange(id);
  };

  const fetchHubOptions = useCallback(async (search: string) => {
    setHubLoading(true);
    try {
      const res = await getListPitstop({
        type: 'HUB',
        hasStaff: true,
        page: '1',
        size: '100',
        ...(search ? { search } : {}),
      });
      setHubOptions(res.data ?? []);
    } catch (_) {
      // Keep the current options on a failed search rather than blanking out
    } finally {
      setHubLoading(false);
    }
  }, []);

  // Debounced (500ms) server-side hub search
  const handleHubSearch = useCallback(
    (term: string) => {
      if (hubSearchDebounceRef.current) {
        clearTimeout(hubSearchDebounceRef.current);
      }
      hubSearchDebounceRef.current = setTimeout(() => {
        fetchHubOptions(term.trim());
      }, 500);
    },
    [fetchHubOptions]
  );

  const doScan = useCallback(
    async (rawValue: string) => {
      if (scanningRef.current) return;

      // A per-item label QR scans as a tracking URL (…/track/{code}); reduce it
      // to the bare code so the item scan matches item_name/invoice as before.
      const itemName = trackCodeFromScan(rawValue).replace(/[\r\n\t]+$/, '');
      if (!itemName) return;

      if (!localHubId) {
        const entry: ScanEntry = {
          id: crypto.randomUUID(),
          po: itemName,
          message: 'Select a hub first',
          state: 'error',
          time: nowTime(),
        };
        setLastScan(entry);
        setFlashState('error');
        playBeep('error');
        setTimeout(() => setFlashState('idle'), 400);
        return;
      }

      scanningRef.current = true;
      setScanning(true);

      const scanUid = crypto.randomUUID();
      let result: ScanItemSuccessResponse | null = null;
      let errorMessage = '';
      let state: ScanState = 'success';

      try {
        result = await scanItem({
          scan_uid: scanUid,
          item_name: itemName,
          hub_id: localHubId,
        });

        if (result.duplicate) {
          state = 'duplicate';
        } else if (!result.item?.zoneCode) {
          // Scanned OK but no zone mapped for this H3 index
          state = 'zone-not-found';
        }
      } catch (err: any) {
        state = 'error';
        errorMessage = err?.message ?? 'Error — retry safe';
      }

      const time = nowTime();
      // Build zone info string for success
      const zoneInfo = result?.item?.zoneCode
        ? result.item.zoneName
          ? `${result.item.zoneCode} · ${result.item.zoneName}`
          : result.item.zoneCode
        : null;

      const entry: ScanEntry = {
        id: scanUid,
        po: itemName,
        message:
          state === 'success'
            ? zoneInfo
              ? `Scanned · ${zoneInfo}`
              : `Scanned · ${result?.item?.code ?? ''}`
            : state === 'zone-not-found'
              ? `Scanned · Zone not found (${result?.item?.destinationH3Index ?? ''})`
              : state === 'duplicate'
                ? 'Already scanned'
                : errorMessage || 'Error — retry safe',
        state,
        time,
        zoneCode: result?.item?.zoneCode ?? null,
        zoneName: result?.item?.zoneName ?? null,
        zoneColor: result?.item?.zoneColor ?? null,
      };

      setLastScan(entry);
      setFeed((prev) => [entry, ...prev].slice(0, MAX_FEED));
      setFlashState(state);
      playBeep(state === 'zone-not-found' ? 'duplicate' : state);

      // Voice announcement — fires after the beep (~300 ms delay feels natural)
      if (voiceEnabled) {
        // Extract only the trailing number from the zone code (e.g. "Z03" → "03")
        // then drop the leading zero so the announcement says "3", not "03".
        // The visual badge still shows the padded "03" from the canonical code.
        const rawCode = result?.item?.zoneCode ?? '';
        const rawNumber = rawCode.match(/(\d+)\D*$/)?.[1] ?? '';
        const zoneNumber = rawNumber ? String(parseInt(rawNumber, 10)) : '';
        setTimeout(() => {
          if (state === 'success') {
            playAnnouncement(zoneNumber || 'Sukses');
          } else if (state === 'duplicate') {
            playAnnouncement(zoneNumber || 'Sudah di scan');
          } else if (state === 'zone-not-found') {
            playAnnouncement('Zona tidak ditemukan');
          } else {
            playAnnouncement('Scan gagal');
          }
        }, 120);
      }

      setTimeout(() => setFlashState('idle'), 400);

      if (state === 'success' || state === 'zone-not-found') {
        onScanSuccess();
      }

      if (state === 'zone-not-found') {
        toast.warning('Zone not found', {
          description: `has no zone mapped. Check zone_cells configuration.`,
        });
      }

      if (state === 'error' && errorMessage) {
        toast.error('Scan error', { description: errorMessage });
      }

      scanningRef.current = false;
      setScanning(false);
      setInputText('');
      setShowSuggestions(false);
      if (inputRef.current) inputRef.current.value = '';
    },
    [localHubId, onScanSuccess, voiceEnabled]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const now = Date.now();
    // A scanner gun types the whole code in a burst and presses Enter itself;
    // it never picks from the dropdown. Searching on each of its ~60
    // keystrokes only re-renders the dropdown and fires a lookup nobody reads,
    // adding load right when the page needs to keep up with the scanner.
    const fromScanner =
      isScannerKeystroke(
        prevInputValueRef.current,
        val,
        now - lastInputChangeAtRef.current
      ) || looksLikeUrlInput(val);
    lastInputChangeAtRef.current = now;
    prevInputValueRef.current = val;
    setInputText(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (fromScanner || val.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSuggestionsLoading(true);
    setShowSuggestions(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await getInboundItems({
          search: val.trim(),
          limit: 8,
          page: 1,
          ...(localHubId ? { hub_id: localHubId } : {}),
        });
        setSuggestions(res.items ?? []);
      } catch {
        setSuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);
  };

  const handleSuggestionClick = (value: string) => {
    setInputText('');
    setShowSuggestions(false);
    if (inputRef.current) inputRef.current.value = '';
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doScan(value), 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = (e.target as HTMLInputElement).value;
      setShowSuggestions(false);
      // Enter ends the entry, so a suggestion search still waiting on its
      // debounce has nothing to show — drop it instead of letting it fire
      // after the scan has already been submitted.
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => doScan(val), 150);
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
    // Focus trap: keep focus on the scan input so rapid scanning isn't
    // interrupted. The hub picker is a searchable dropdown that manages its
    // own focus, so it's no longer part of this Tab cycle.
    if (e.key === 'Tab') {
      e.preventDefault();
      setShowSuggestions(false);
      document.getElementById('scan-input')?.focus();
    }
  };

  const inputBorderClass =
    flashState === 'success'
      ? 'border-(--state-success-base) bg-success-lighter'
      : flashState === 'duplicate' || flashState === 'zone-not-found'
        ? 'border-(--state-warning-base) bg-warning-lighter'
        : flashState === 'error'
          ? 'border-(--state-error-base) bg-error-lighter'
          : 'border-accent bg-(--theme-accent-alpha-10)';

  const selectedHub =
    hubOptions.find((h) => h.id === localHubId) ??
    hubs.find((h) => h.id === localHubId);

  const header = (
    <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-soft-200 flex-shrink-0">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-(--theme-accent-alpha-10)">
          <i className="ri-qr-scan-line text-accent text-base" />
        </div>
        <div>
          <SheetTitle className="text-sm font-bold text-text-strong-950 leading-none">
            Scan Panel
          </SheetTitle>
          <div className="text-[11px] text-text-sub-600 mt-0.5">
            {selectedHub ? selectedHub.name : 'No hub selected'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <IconButton
          type="button"
          tone={voiceEnabled ? 'primary' : 'neutral'}
          style="ghost"
          size="sm"
          onClick={toggleVoice}
          aria-label={
            voiceEnabled
              ? 'Matikan suara pengumuman'
              : 'Aktifkan suara pengumuman'
          }
          title={
            voiceEnabled
              ? 'Voice on — click to mute'
              : 'Voice off — click to enable'
          }
        >
          <i
            className={voiceEnabled ? 'ri-volume-up-line' : 'ri-volume-mute-line'}
          />
        </IconButton>
        <span className="text-[9px] text-text-soft-400 font-mono tracking-wide ml-1">
          Esc
        </span>
        <IconButton
          type="button"
          tone="neutral"
          style="ghost"
          size="sm"
          onClick={onClose}
          aria-label="Close scan panel"
        >
          <i className="ri-close-line text-lg" />
        </IconButton>
      </div>
    </div>
  );

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent
        side="right"
        size="md"
        showClose={false}
        className="p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Focus/clicks into the body-portaled hub dropdown must not close us.
          const t = e.target as HTMLElement | null;
          if (t?.closest?.('[data-searchable-dropdown-portal]')) {
            e.preventDefault();
          }
        }}
      >
        {header}
        <div className="flex flex-1 flex-col overflow-hidden">
        {/* Hub selector */}
        <div className="px-5 py-4 border-b border-stroke-soft-200 flex-shrink-0">
          <label
            id="scan-hub-label"
            className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
          >
            Hub
          </label>
          <SearchSelect
            options={hubOptions.map((h) => ({ value: h.id, label: h.name }))}
            value={localHubId ?? ''}
            selectedLabelFallback={selectedHub?.name}
            onChange={handleHubChange}
            onSearchChange={handleHubSearch}
            disableLocalFilter
            loading={hubLoading}
            placeholder="Search hub..."
            emptyMessage="No hubs found"
          />
        </div>

        {/* Barcode input */}
        <div className="px-5 py-4 border-b border-stroke-soft-200 flex-shrink-0">
          <label
            htmlFor="scan-input"
            className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
          >
            Barcode / PO ID
          </label>
          <div className="relative">
            <input
              ref={inputRef}
              id="scan-input"
              type="text"
              autoComplete="off"
              spellCheck={false}
              disabled={scanning || !localHubId}
              placeholder={
                localHubId ? 'Scan or type PO ID...' : 'Select a hub first...'
              }
              aria-label="Scan barcode or type PO ID"
              value={inputText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              className={`w-full rounded-lg border-2 px-3 py-2.5 font-mono text-sm text-text-strong-950 transition-colors focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:opacity-60 ${inputBorderClass}`}
            />
            {showSuggestions &&
              (suggestionsLoading || suggestions.length > 0) && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-stroke-soft-200 bg-white shadow-lg overflow-hidden">
                  {suggestionsLoading ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 text-[12px] text-text-soft-400">
                      <i className="ri-loader-4-line animate-spin text-accent text-[13px]" />
                      Searching...
                    </div>
                  ) : (
                    suggestions.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSuggestionClick(item.itemName)}
                        className="flex w-full flex-col px-3 py-2 text-left hover:bg-bg-weak-50 transition-colors border-b border-stroke-soft-200 last:border-0"
                      >
                        <span className="font-mono text-[12px] text-text-strong-950 truncate">
                          {item.shipment?.externalDeliveryUid
                            ? `${item.itemName} - ${item.shipment.externalDeliveryUid}`
                            : item.itemName}
                        </span>
                        {item.shipment?.providerOrderId && (
                          <span className="font-mono text-[10px] text-text-soft-400 truncate">
                            {item.shipment.providerOrderId}
                          </span>
                        )}
                        {item.invoice && item.invoice !== item.itemName && (
                          <span className="font-mono text-[10px] text-text-soft-400 truncate">
                            Invoice: {item.invoice}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-[10px] text-text-soft-400 flex items-center gap-1.5">
              {localHubId ? (
                <>
                  <i className="ri-focus-3-line text-accent text-[11px]" />
                  Always focused ·{' '}
                  <kbd className="rounded bg-bg-weak-50 border border-stroke-soft-200 px-1 py-0.5 text-[9px] font-mono text-text-sub-600">
                    Enter
                  </kbd>{' '}
                  to scan
                </>
              ) : (
                <>
                  <i className="ri-error-warning-line text-(--state-warning-base) text-[11px]" />
                  Select a hub above to enable scanning
                </>
              )}
            </span>
            {scanning && (
              <span className="text-[10px] text-accent font-semibold">
                Scanning...
              </span>
            )}
          </div>
        </div>

        {/* Last scan result */}
        {lastScan && (
          <div className="px-5 py-3 border-b border-stroke-soft-200 flex-shrink-0">
            <div
              className={`flex items-start gap-3 rounded-[12px] border p-4 ${
                lastScan.state === 'success'
                  ? 'bg-success-lighter border-success-light'
                  : lastScan.state === 'duplicate' ||
                      lastScan.state === 'zone-not-found'
                    ? 'bg-warning-lighter border-warning-light'
                    : 'bg-error-lighter border-error-light'
              }`}
            >
              {/* Icon */}
              <div
                className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[8px] ${
                  lastScan.state === 'success'
                    ? 'bg-(--state-success-base)'
                    : lastScan.state === 'duplicate' ||
                        lastScan.state === 'zone-not-found'
                      ? 'bg-(--state-warning-base)'
                      : 'bg-(--state-error-base)'
                }`}
              >
                <i
                  className={`text-white text-[15px] leading-none ${
                    lastScan.state === 'success'
                      ? 'ri-check-line'
                      : 'ri-alert-line'
                  }`}
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Tracking number */}
                <p className="font-mono text-[13px] font-bold tracking-tight text-text-strong-950 truncate">
                  {lastScan.po}
                </p>

                {/* Zone — primary visual focus on success/duplicate */}
                {(lastScan.state === 'success' ||
                  lastScan.state === 'duplicate') &&
                  lastScan.zoneCode && (
                    <div className="mt-2 flex items-center gap-2">
                      <span
                        className="rounded-full px-2.5 py-0.5 font-mono text-[12px] font-semibold"
                        style={
                          lastScan.zoneColor
                            ? {
                                backgroundColor: `${lastScan.zoneColor}20`,
                                color: lastScan.zoneColor,
                              }
                            : {
                                backgroundColor: 'rgba(94,42,172,0.12)',
                                color: '#5E2AAC',
                              }
                        }
                      >
                        {lastScan.zoneCode}
                      </span>
                      {lastScan.zoneName && (
                        <span className="text-[13px] font-semibold text-text-sub-600 truncate">
                          {lastScan.zoneName}
                        </span>
                      )}
                    </div>
                  )}

                {/* Status line */}
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span
                    className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                    style={{
                      backgroundColor:
                        lastScan.state === 'success'
                          ? (lastScan.zoneColor ?? '#8B5CF6')
                          : lastScan.state === 'error'
                            ? '#EF4444'
                            : '#F59E0B',
                    }}
                  />
                  <span className="text-[11px] text-text-sub-600">
                    {lastScan.state === 'success'
                      ? 'Scanned'
                      : lastScan.message}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent scans feed */}
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between px-5 py-2 border-b border-stroke-soft-200 bg-bg-weak-50/50 flex-shrink-0">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-text-soft-400">
              Recent Scans
            </span>
            <span className="rounded-full bg-(--theme-accent-alpha-10) text-accent text-[10px] font-bold px-2 py-0.5 font-mono">
              {feed.length}
            </span>
          </div>
          <div className="overflow-y-auto flex-1">
            {feed.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                <i className="ri-qr-scan-line text-3xl text-text-soft-400" />
                <p className="text-sm font-semibold text-text-sub-600">
                  No scans yet
                </p>
                <p className="text-[11px] text-text-soft-400">
                  Select a hub, then start scanning.
                </p>
              </div>
            ) : (
              feed.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-start gap-2.5 px-5 py-3 border-b border-stroke-soft-200 hover:bg-bg-weak-50/60 transition-colors"
                >
                  {/* State icon */}
                  <div
                    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-[4px] ${
                      entry.state === 'success'
                        ? 'bg-(--state-success-base)'
                        : entry.state === 'duplicate' ||
                            entry.state === 'zone-not-found'
                          ? 'bg-(--state-warning-base)'
                          : 'bg-(--state-error-base)'
                    }`}
                  >
                    <i
                      className={`text-white text-[10px] leading-none ${
                        entry.state === 'success'
                          ? 'ri-check-line'
                          : 'ri-alert-line'
                      }`}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Package ID */}
                    <p className="font-mono text-[11px] font-bold text-text-strong-950 truncate tracking-tight">
                      {entry.po}
                    </p>

                    {/* Zone pill + name */}
                    {(entry.state === 'success' ||
                      entry.state === 'duplicate') &&
                      entry.zoneCode && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <span
                            className="rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold"
                            style={
                              entry.zoneColor
                                ? {
                                    backgroundColor: `${entry.zoneColor}20`,
                                    color: entry.zoneColor,
                                  }
                                : {
                                    backgroundColor: 'rgba(94,42,172,0.12)',
                                    color: '#5E2AAC',
                                  }
                            }
                          >
                            {entry.zoneCode}
                          </span>
                          {entry.zoneName && (
                            <span className="text-[11px] font-semibold text-text-sub-600 truncate">
                              {entry.zoneName}
                            </span>
                          )}
                        </div>
                      )}

                    {/* Status */}
                    <div className="mt-0.5 flex items-center gap-1">
                      <span
                        className="h-1 w-1 flex-shrink-0 rounded-full"
                        style={{
                          backgroundColor:
                            entry.state === 'success'
                              ? (entry.zoneColor ?? '#8B5CF6')
                              : entry.state === 'error'
                                ? '#EF4444'
                                : '#F59E0B',
                        }}
                      />
                      <span className="text-[10px] text-text-soft-400">
                        {entry.state === 'success' ? 'Scanned' : entry.message}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      </SheetContent>
    </Sheet>
  );
};

export default ScanPanel;
